import { Injectable, signal } from '@angular/core';

/**
 * [HM-banner] Tracks the most recent `X-Hub-Sync-Stale` header value
 * surfaced by the Store Edge Agent.
 *
 * The hub injects `X-Hub-Sync-Stale: true` on read responses whose
 * underlying snapshot has not refreshed within its TTL — see
 * `api_server_service.dart` middleware (P1.7). The SPA renders a
 * non-blocking advisory in the offline banner so the operator knows
 * the data is older than usual but writes keep flowing.
 *
 * The hub also emits `X-Hub-Sync-Stale-Resources: pricing,charges`
 * (comma-separated resource keys) when known; we surface that for
 * tooltip-level detail without parsing further.
 *
 * State decays automatically: if no stale header is seen for a
 * window, we clear it on the next non-stale response so the banner
 * doesn't latch forever.
 */
@Injectable({ providedIn: 'root' })
export class HubStalenessService {
  /** True whenever the most recent hub response signalled stale sync state. */
  readonly stale = signal(false);
  /** Resource keys flagged as stale by the hub (best-effort, may be empty). */
  readonly resources = signal<readonly string[]>([]);

  /**
   * Called by the offline interceptor after a successful hub response.
   * `value` is the raw header string ('true'/'false') and `resources`
   * is the comma-separated list of stale resource keys.
   */
  recordResponseHeaders(value: string | null, resources: string | null): void {
    const isStale = (value ?? '').trim().toLowerCase() === 'true';
    this.stale.set(isStale);
    if (!isStale) {
      this.resources.set([]);
      return;
    }
    const keys = (resources ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    this.resources.set(keys);
  }
}
